import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, CheckCircle2, Lock, Sparkles, Send, Star, Heart, ThumbsDown,
  AlertCircle, Loader2, Hourglass, MessageCircleHeart,
} from "lucide-react";

const PACING_OPTIONS = [
  { value: "too_short", label: "Too short" },
  { value: "right", label: "Just right" },
  { value: "too_long", label: "Too long" },
];

const NPS_LABELS = ["Not at all", "Definitely yes"];

function StarRating({ value, onChange, testIdPrefix, max = 5 }) {
  return (
    <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-stars`}>
      {Array.from({ length: max }).map((_, i) => {
        const v = i + 1;
        const filled = v <= (value || 0);
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            data-testid={`${testIdPrefix}-star-${v}`}
            className={`p-1 transition-transform hover:scale-110 ${filled ? "text-[#E53935]" : "text-slate-300 hover:text-slate-400"}`}
            aria-label={`${v} of ${max}`}
          >
            <Star size={22} fill={filled ? "currentColor" : "none"} />
          </button>
        );
      })}
      <span className="ml-2 text-xs text-slate-500">{value ? `${value}/${max}` : "Tap to rate"}</span>
    </div>
  );
}

function NpsScale({ value, onChange }) {
  return (
    <div data-testid="nps-scale">
      <div className="grid grid-cols-11 gap-1.5">
        {Array.from({ length: 11 }).map((_, i) => {
          const sel = value === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChange(i)}
              data-testid={`nps-${i}`}
              className={`h-10 rounded-md text-sm font-semibold border transition-all ${
                sel
                  ? "bg-[#222222] text-white border-[#222222] scale-105 shadow"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              {i}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-[11px] text-slate-500">
        <span>0 — {NPS_LABELS[0]}</span><span>{NPS_LABELS[1]} — 10</span>
      </div>
    </div>
  );
}

export default function Feedback() {
  const nav = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(null); // their previously-submitted feedback (if any)
  const [submitting, setSubmitting] = useState(false);

  const [overall, setOverall] = useState({
    nps: null,
    content_relevance: 0,
    most_loved: "",
    most_hated: "",
    suggestions: "",
  });
  const [modulesById, setModulesById] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [{ data: elig }, { data: mine }] = await Promise.all([
          api.get("/feedback/eligibility"),
          api.get("/feedback/me"),
        ]);
        setEligibility(elig);
        if (mine) setSubmitted(mine);
        // seed module entries
        const seed = {};
        (elig.modules || []).forEach((m) => {
          const prev = (mine?.modules || []).find((x) => x.module_id === m.module_id);
          seed[m.module_id] = prev || {
            relevance: 0, pacing: "right", worked_well: "", could_be_better: "", suggestions: "",
          };
        });
        setModulesById(seed);
        if (mine?.overall) setOverall(mine.overall);
      } catch (_) {
        toast.error("Couldn't load feedback form");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const locked = !!submitted?.locked;
  const eligible = eligibility?.eligible;
  const totalMods = eligibility?.total_modules || 0;
  const doneMods = eligibility?.completed_modules || 0;

  // group modules by course for nicer rendering
  const grouped = useMemo(() => {
    const map = new Map();
    (eligibility?.modules || []).forEach((m) => {
      const key = m.course_id || "unknown";
      if (!map.has(key)) map.set(key, { course_title: m.course_title || "Course", modules: [] });
      map.get(key).modules.push(m);
    });
    return Array.from(map.values());
  }, [eligibility]);

  const updateMod = (mid, patch) => {
    if (locked) return;
    setModulesById((cur) => ({ ...cur, [mid]: { ...cur[mid], ...patch } }));
  };

  const validate = () => {
    if (overall.nps === null || overall.nps === undefined) return "Please answer the recommendation (NPS) question.";
    if (!overall.content_relevance) return "Please rate the overall content relevance.";
    for (const m of eligibility?.modules || []) {
      const fb = modulesById[m.module_id];
      if (!fb || !fb.relevance) return `Please rate the relevance of "${m.module_title}".`;
    }
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const modulesArr = (eligibility?.modules || []).map((m) => ({
        module_id: m.module_id,
        ...modulesById[m.module_id],
      }));
      await api.post("/feedback", { overall, modules: modulesArr });
      toast.success("Thank you! Your feedback has been recorded 🎉");
      // refresh
      const { data: mine } = await api.get("/feedback/me");
      setSubmitted(mine);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Render branches ---------- */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="animate-spin" size={16}/> Loading feedback…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <Link to="/learn" className="text-sm text-slate-600 hover:text-[#E53935] inline-flex items-center gap-1.5">
            <ArrowLeft size={14}/> Back to learning
          </Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero / intro */}
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-2 inline-flex items-center gap-2">
            <MessageCircleHeart size={12}/> Your feedback shapes v2
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[#222222] mb-4">Help us make this better.</h1>
          <p className="text-slate-600 max-w-3xl leading-relaxed">
            This is <strong>our first attempt</strong> at building a corporate L&amp;D platform as a new company —
            and honestly, we&apos;d rather hear the rough edges than pretend everything&apos;s perfect.
            What you share here goes directly into the next version. No question is biased toward
            a &ldquo;good&rdquo; answer; tell us what worked, what didn&apos;t, and what you wish existed.
          </p>
        </div>

        {/* Gating state — ineligible */}
        {!eligible && !locked && (
          <Card className="p-6 mb-6 border-amber-200 bg-amber-50" data-testid="feedback-locked-banner">
            <div className="flex items-start gap-3">
              <Hourglass size={20} className="text-amber-700 mt-0.5"/>
              <div>
                <div className="font-semibold text-amber-900 mb-1">Feedback unlocks once you&apos;ve read through every module</div>
                <p className="text-sm text-amber-800 mb-3">
                  You&apos;re {doneMods}/{totalMods} modules through. Quizzes aren&apos;t required — just read each module
                  to at least {Math.round(eligibility?.read_threshold || 80)}% and the form opens.
                </p>
                <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-auto pr-1">
                  {(eligibility?.modules || []).map((m) => (
                    <div
                      key={m.module_id}
                      data-testid={`progress-row-${m.module_id}`}
                      className={`flex items-center justify-between text-xs px-3 py-2 rounded border ${
                        m.read ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"
                      }`}
                    >
                      <div className="truncate mr-3">
                        <div className={m.read ? "text-emerald-700 font-medium" : "text-slate-700"}>{m.module_title}</div>
                        <div className="text-[10px] text-slate-500">{m.course_title}</div>
                      </div>
                      <div className={`flex items-center gap-1 ${m.read ? "text-emerald-700" : "text-slate-500"}`}>
                        {m.read ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>}
                        {Math.round(m.scroll_pct)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Locked / already submitted */}
        {locked && (
          <Card className="p-6 mb-6 border-emerald-200 bg-emerald-50" data-testid="feedback-already-submitted-banner">
            <div className="flex items-start gap-3">
              <Lock size={20} className="text-emerald-700 mt-0.5"/>
              <div>
                <div className="font-semibold text-emerald-900 mb-1">Thank you — your feedback is already on its way to the team.</div>
                <p className="text-sm text-emerald-800">
                  Submitted on {new Date(submitted.submitted_at).toLocaleString()}. Below is a read-only copy of what you sent.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Form / view-only — only show form when (eligible OR locked) so users can see their old submission */}
        {(eligible || locked) && (
          <fieldset disabled={locked || submitting} className="space-y-10">
            {/* ---------- Overall section ---------- */}
            <Card className="p-7" data-testid="overall-section">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-[#E53935]"/>
                <h2 className="font-display text-2xl text-[#222222]">Overall academy</h2>
              </div>
              <p className="text-sm text-slate-500 mb-6">First, the big picture across everything you&apos;ve read.</p>

              <div className="space-y-7">
                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-2">
                    How likely are you to recommend this academy to a colleague?
                  </label>
                  <NpsScale value={overall.nps} onChange={(v) => setOverall((o) => ({ ...o, nps: v }))}/>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-2">
                    Overall, how <strong>relevant</strong> was the content to your day-to-day role?
                  </label>
                  <StarRating
                    value={overall.content_relevance}
                    onChange={(v) => setOverall((o) => ({ ...o, content_relevance: v }))}
                    testIdPrefix="overall-relevance"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-800 mb-2 inline-flex items-center gap-1.5">
                      <Heart size={14} className="text-[#E53935]"/> What did you love most?
                    </label>
                    <Textarea
                      data-testid="overall-most-loved"
                      value={overall.most_loved}
                      onChange={(e) => setOverall((o) => ({ ...o, most_loved: e.target.value }))}
                      placeholder="Be specific — a feature, a module, a moment."
                      rows={4}
                      maxLength={2000}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-800 mb-2 inline-flex items-center gap-1.5">
                      <ThumbsDown size={14} className="text-slate-700"/> What annoyed or frustrated you?
                    </label>
                    <Textarea
                      data-testid="overall-most-hated"
                      value={overall.most_hated}
                      onChange={(e) => setOverall((o) => ({ ...o, most_hated: e.target.value }))}
                      placeholder="Don't be polite. We need this part."
                      rows={4}
                      maxLength={2000}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 mb-2">
                    Any features or content you wish existed?
                  </label>
                  <Textarea
                    data-testid="overall-suggestions"
                    value={overall.suggestions}
                    onChange={(e) => setOverall((o) => ({ ...o, suggestions: e.target.value }))}
                    placeholder="Wishlist mode — anything from new modules to UX tweaks."
                    rows={4}
                    maxLength={4000}
                  />
                </div>
              </div>
            </Card>

            {/* ---------- Module-by-module ---------- */}
            <div>
              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#E53935] font-bold mb-1">Module by module</div>
                <h2 className="font-display text-2xl text-[#222222]">Tell us about each one</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Quick ratings + a couple of optional notes. {grouped.length} course{grouped.length === 1 ? "" : "s"},
                  {" "}{totalMods} module{totalMods === 1 ? "" : "s"}.
                </p>
              </div>

              {grouped.map((g, gi) => (
                <div key={gi} className="mb-7">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 border-l-4 border-[#E53935] pl-3">
                    {g.course_title}
                  </div>
                  <div className="space-y-4">
                    {g.modules.map((m) => {
                      const fb = modulesById[m.module_id] || {};
                      return (
                        <Card
                          key={m.module_id}
                          data-testid={`module-feedback-${m.module_id}`}
                          className="p-5 border-slate-200"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                            <div className="min-w-0">
                              <div className="font-display text-lg text-[#222222] truncate">{m.module_title}</div>
                              <div className="text-[11px] text-slate-500 mt-0.5">Read {Math.round(m.scroll_pct)}%</div>
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1.5">Content relevance</label>
                              <StarRating
                                value={fb.relevance}
                                onChange={(v) => updateMod(m.module_id, { relevance: v })}
                                testIdPrefix={`mod-${m.module_id}-relevance`}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1.5">Pacing</label>
                              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                                {PACING_OPTIONS.map((p) => (
                                  <button
                                    key={p.value}
                                    type="button"
                                    data-testid={`mod-${m.module_id}-pacing-${p.value}`}
                                    onClick={() => updateMod(m.module_id, { pacing: p.value })}
                                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                                      fb.pacing === p.value ? "bg-white text-[#222222] shadow font-semibold" : "text-slate-600"
                                    }`}
                                  >
                                    {p.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="grid md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1.5">What worked well</label>
                              <Textarea
                                data-testid={`mod-${m.module_id}-worked-well`}
                                value={fb.worked_well || ""}
                                onChange={(e) => updateMod(m.module_id, { worked_well: e.target.value })}
                                rows={3}
                                maxLength={2000}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1.5">What could be better</label>
                              <Textarea
                                data-testid={`mod-${m.module_id}-could-be-better`}
                                value={fb.could_be_better || ""}
                                onChange={(e) => updateMod(m.module_id, { could_be_better: e.target.value })}
                                rows={3}
                                maxLength={2000}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1.5">Suggestions for this module</label>
                              <Textarea
                                data-testid={`mod-${m.module_id}-suggestions`}
                                value={fb.suggestions || ""}
                                onChange={(e) => updateMod(m.module_id, { suggestions: e.target.value })}
                                rows={3}
                                maxLength={2000}
                              />
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Submit */}
            {!locked && (
              <div className="sticky bottom-4 z-10">
                <Card className="p-5 border-[#E53935]/30 shadow-xl bg-white flex items-center justify-between gap-4">
                  <div className="text-xs text-slate-600">
                    One-shot survey — once submitted, your answers are locked and shared with the team.
                  </div>
                  <Button
                    data-testid="submit-feedback-button"
                    onClick={onSubmit}
                    disabled={submitting}
                    className="bg-[#E53935] hover:bg-[#c62828] text-white h-11 px-6"
                  >
                    {submitting ? <><Loader2 className="animate-spin mr-2" size={14}/> Submitting…</>
                      : <><Send size={14} className="mr-2"/> Submit feedback</>}
                  </Button>
                </Card>
              </div>
            )}
          </fieldset>
        )}
      </section>
    </div>
  );
}
